import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, LogIn, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please enter email/phone and password');
      return;
    }

    setLoading(true);
    try {
      console.log('Attempting login for:', username);
      const response = await axios.post(`${API}/auth/login`, { username, password }, { withCredentials: true });
      console.log('Login response received:', response.status);
      const { user, session_token } = response.data;
      
      localStorage.setItem('user_email', user.email);
      localStorage.setItem('user_status', user.status);
      localStorage.setItem('session_token', session_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('Login successful!');
      
      if (user.status === 'rejected') {
        toast.error('Your account has been rejected');
        localStorage.clear();
      } else if (user.must_change_password) {
        // Redirect to change password page for first-time login
        navigate('/change-password', { 
          state: { 
            email: user.email, 
            name: user.first_name || user.name 
          } 
        });
      } else if (!user.is_onboarded) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      const message = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4 text-[#64748B] hover:text-[#0F172A] px-0"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card className="border-[#E2E8F0] shadow-none">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-brand" />
              <CardTitle className="font-heading text-xl font-bold text-[#0F172A]">
                LOGIN
              </CardTitle>
            </div>
            <CardDescription className="text-[#64748B] text-sm">
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-[#0F172A] font-medium text-sm">Email or Phone Number</Label>
                  <Input
                    data-testid="login-username"
                    id="username"
                    type="text"
                    placeholder="Enter your email or phone number"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="bg-white border-[#E2E8F0] h-12 text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#0F172A] font-medium text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      data-testid="login-password"
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-white border-[#E2E8F0] h-12 text-base pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <Button
                  data-testid="login-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-brand hover:bg-brand-hover text-white h-12 font-semibold rounded-md text-base"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <button
            onClick={() => navigate('/signup')}
            className="text-sm text-[#64748B] hover:text-[#0F172A]"
          >
            Don't have an account? <span className="font-semibold text-brand">Sign up</span>
          </button>
        </div>
      </div>
    </div>
  );
}
